import React from 'react';
import { Modal, Form, Input, InputNumber, Select, DatePicker, Button, Space, Typography, Row, Col, notification } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import request from '../../../lib/request';

import { money } from '../../../lib/format';
/**
 * Draft invoice editor (create or edit-while-DRAFT). Lines are a Form.List;
 * picking a VAT Code copies its rate onto the line, the same way the backend
 * freezes the rate on the stored line.
 */
export default function InvoiceEditorModal({ open, onCancel, onSaved, companyId, clients, services, vatCodes, engagements, initial }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    if (initial) {
      form.setFieldsValue({
        client_id: initial.client_id,
        engagement_id: initial.engagement_id,
        invoice_date: initial.invoice_date ? moment(initial.invoice_date) : null,
        due_date: initial.due_date ? moment(initial.due_date) : null,
        period_label: initial.period_label,
        payment_instructions: initial.payment_instructions,
        notes: initial.notes,
        lines: (initial.InvoiceLines || []).map((l) => ({
          service_id: l.service_id,
          description: l.description,
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
          vat_code_id: l.vat_code_id,
          vat_rate: Number(l.vat_rate),
        })),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        invoice_date: moment(),
        due_date: moment().add(30, 'days'),
        lines: [{ quantity: 1, unit_price: 0, vat_rate: 0 }],
      });
    }
  }, [open, initial, form]);

  const watchedLines = Form.useWatch('lines', form) || [];
  const totals = watchedLines.reduce(
    (acc, l) => {
      const net = Number(l?.quantity || 0) * Number(l?.unit_price || 0);
      const vat = net * (Number(l?.vat_rate || 0) / 100);
      return { net: acc.net + net, vat: acc.vat + vat };
    },
    { net: 0, vat: 0 }
  );

  const handleOk = () => {
    form.validateFields().then((values) => {
      const payload = {
        ...values,
        company_id: companyId,
        invoice_date: values.invoice_date.format('YYYY-MM-DD'),
        due_date: values.due_date.format('YYYY-MM-DD'),
        engagement_id: values.engagement_id || null,
        period_label: values.period_label || null,
        payment_instructions: values.payment_instructions || null,
        notes: values.notes || null,
        lines: values.lines.map((l) => ({ ...l, service_id: l.service_id || null, vat_code_id: l.vat_code_id || null })),
      };
      const call = initial ? request.put(`/invoices/${initial.id}`, payload) : request.post('/invoices', payload);
      setSaving(true);
      call
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: initial ? 'Invoice updated' : `Draft ${r.data.invoice_number} created` });
            onSaved(r.data);
          } else {
            notification.error({ message: r.message });
          }
        })
        .finally(() => setSaving(false));
    });
  };

  const clientEngagements = engagements.filter((e) => e.client_id === form.getFieldValue('client_id'));

  return (
    <Modal title={initial ? `Edit ${initial.invoice_number}` : 'New Invoice'} open={open} onCancel={onCancel} onOk={handleOk} confirmLoading={saving} width={900}>
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="client_id" label="Client" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                disabled={!!initial}
                options={clients.map((c) => ({ value: c.id, label: c.client_name }))}
                onChange={() => form.setFieldsValue({ engagement_id: null })}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="engagement_id" label="Service Engagement (optional)">
              <Select
                allowClear
                options={clientEngagements.map((e) => ({ value: e.id, label: `${e.Service?.service_name || 'Service'} - ${e.frequency}` }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="invoice_date" label="Invoice Date" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="due_date" label="Due Date" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="period_label" label="Billing Period">
              <Input placeholder="e.g. 09/2026, Q3 2026" />
            </Form.Item>
          </Col>
        </Row>

        <Typography.Title level={5}>Lines</Typography.Title>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <Row gutter={8} key={field.key} align="top">
                  <Col span={8}>
                    <Form.Item {...field} name={[field.name, 'description']} rules={[{ required: true, message: 'Description' }]}>
                      <Input placeholder="Description" />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item {...field} name={[field.name, 'service_id']}>
                      <Select allowClear placeholder="Service" options={services.map((s) => ({ value: s.id, label: s.service_name }))} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item {...field} name={[field.name, 'quantity']} rules={[{ required: true, message: 'Qty' }]}>
                      <InputNumber min={0.01} step={0.5} style={{ width: '100%' }} placeholder="Qty" />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item {...field} name={[field.name, 'unit_price']} rules={[{ required: true, message: 'Price' }]}>
                      <InputNumber min={0} step={10} style={{ width: '100%' }} placeholder="Unit price" />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item {...field} name={[field.name, 'vat_code_id']}>
                      <Select
                        allowClear
                        placeholder="VAT"
                        options={vatCodes.map((v) => ({ value: v.id, label: `${v.code} (${Number(v.rate)}%)` }))}
                        onChange={(value) => {
                          const code = vatCodes.find((v) => v.id === value);
                          const lines = form.getFieldValue('lines');
                          lines[field.name] = { ...lines[field.name], vat_rate: code ? Number(code.rate) : 0 };
                          form.setFieldsValue({ lines });
                        }}
                      />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'vat_rate']} hidden>
                      <InputNumber />
                    </Form.Item>
                  </Col>
                  <Col span={1}>
                    <Button icon={<DeleteOutlined />} danger type="text" onClick={() => remove(field.name)} disabled={fields.length === 1} />
                  </Col>
                </Row>
              ))}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ quantity: 1, unit_price: 0, vat_rate: 0 })} block>
                Add line
              </Button>
            </>
          )}
        </Form.List>

        <Space style={{ marginTop: 16, width: '100%', justifyContent: 'flex-end' }} size="large">
          <Typography.Text>Net: {money(totals.net)}</Typography.Text>
          <Typography.Text>VAT: {money(totals.vat)}</Typography.Text>
          <Typography.Text strong>Total: {money(totals.net + totals.vat)}</Typography.Text>
        </Space>

        <Form.Item name="payment_instructions" label="Payment Instructions" style={{ marginTop: 16 }}>
          <Input.TextArea rows={2} placeholder="Bank name, IBAN, reference to quote..." />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
